import moment from 'moment'
import type { Log, Order, TPOSLocation } from 'types'
import { getFormattedTimeSince } from './dates'

export const isShipmentGenerated = (order: Order): boolean => Boolean(order.shipments?.length)
export const isLabelPrinted = (order: Order): boolean =>
  order.logs.some(log => log.event === 'print' && log.message.includes('Printed shipping label'))

export const shippingLabelRequests = (order: Order): Log | undefined =>
  order?.logs?.find(log => log.message === 'Shipping Label Requested')
export const isShippingLabelRequested = (order: Order): boolean => Boolean(shippingLabelRequests(order))
export const shippingLabelPending = (order: Order): boolean =>
  isShippingLabelRequested(order) && !isShipmentGenerated(order)

export enum ShippingLabelStatus {
  Pending = 'Pending',
  NotRequested = 'Not Requested',
  Ready = 'Ready',
}

export const getShippingLabelStatus = (order: Order): ShippingLabelStatus => {
  if (isShipmentGenerated(order)) {
    return ShippingLabelStatus.Ready
  }
  if (shippingLabelPending(order)) {
    return ShippingLabelStatus.Pending
  }
  return ShippingLabelStatus.NotRequested
}

type formatCreatedDateType = {
  date: string
  greaterThan48: string
}
export const formatCreatedDate = (createdAt?: Date): formatCreatedDateType => {
  if (!createdAt) {
    return { date: '-', greaterThan48: '' }
  }
  const dateCreated = moment(createdAt)
  const date = dateCreated.format('MM/DD/YYYY hh:mmA')
  const greaterThan48 = getFormattedTimeSince(createdAt)

  return { date, greaterThan48 }
}

export const formatCreatedDateV2 = (createdAt?: Date): formatCreatedDateType => {
  if (!createdAt) {
    return { date: '-', greaterThan48: '' }
  }
  const dateCreated = moment(createdAt)
  const timezoneShortname = moment().tz(moment.tz.guess()).format('z')
  const date = `${dateCreated.format('MMM D, YYYY hh:mmA')} ${timezoneShortname}`
  const greaterThan48 = getFormattedTimeSince(createdAt)

  return { date, greaterThan48 }
}

export const isOrderReadOnly = (order: Order | undefined): boolean => {
  if (order && isLocationReadOnly(order.location)) {
    return true
  }
  return false
}

export const isLocationReadOnly = (location: TPOSLocation | undefined): boolean => {
  if (location && location.isThirdParty) {
    return true
  }
  return false
}

/**
 * Normalizes an order ID string by converting the literal string 'undefined' to actual undefined,
 * while preserving valid order ID strings
 * @param orderId - The order ID string, null, or undefined
 * @returns The order ID if it's a valid string, otherwise undefined
 */
export const normalizeOrderId = (orderId?: string | null): string | undefined => {
  return orderId && orderId !== 'undefined' ? orderId : undefined
}
